import { Center, Heading, Text, VStack } from '@gluestack-ui/themed';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ROUTES, ROUTE_PATTERNS } from '@/config/routes';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { LegacyModelListRoute, LegacyModelRoute } from '@/features/workspace/components/LegacyModelRoutes';
import { ModelFrame } from '@/features/workspace/components/ModelFrame';
import { WorkspaceGate } from '@/features/workspace/components/WorkspaceGate';
import { SubscriberSync } from '@/features/following/components/SubscriberSync';
import { LoadingState } from '@/shared/components/feedback/LoadingState';
import { AuthenticatedShell } from './AuthenticatedShell';

const TasksPage = lazy(() =>
  import('@/features/tasks/pages/TasksPage').then((module) => ({ default: module.TasksPage }))
);

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((module) => ({ default: module.LoginPage }))
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  }))
);
const ProfilePage = lazy(() =>
  import('@/features/profile/pages/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const NotificationSettingsPage = lazy(() =>
  import('@/features/following/pages/NotificationSettingsPage').then((module) => ({
    default: module.NotificationSettingsPage,
  }))
);
const FamiliesPage = lazy(() =>
  import('@/features/families/pages/FamiliesPage').then((module) => ({
    default: module.FamiliesPage,
  }))
);
const FamilyDetailPage = lazy(() =>
  import('@/features/families/pages/FamilyDetailPage').then((module) => ({
    default: module.FamilyDetailPage,
  }))
);
const ProjectFamilyDetailPage = lazy(() =>
  import('@/features/families/pages/ProjectFamilyDetailPage').then((module) => ({
    default: module.ProjectFamilyDetailPage,
  }))
);
const ProjectsPage = lazy(() =>
  import('@/features/projects/pages/ProjectsPage').then((module) => ({
    default: module.ProjectsPage,
  }))
);
const ProjectDetailPage = lazy(() =>
  import('@/features/projects/pages/ProjectDetailPage').then((module) => ({
    default: module.ProjectDetailPage,
  }))
);
const WorkspaceProjectsPage = lazy(() =>
  import('@/features/workspace/pages/WorkspaceProjectsPage').then((module) => ({
    default: module.WorkspaceProjectsPage,
  }))
);
const ProjectHomePage = lazy(() =>
  import('@/features/workspace/pages/ProjectHomePage').then((module) => ({
    default: module.ProjectHomePage,
  }))
);
const DocumentsPage = lazy(() =>
  import('@/features/documents/pages/DocumentsPage').then((module) => ({
    default: module.DocumentsPage,
  }))
);
const SheetsPage = lazy(() =>
  import('@/features/documents/pages/SheetsPage').then((module) => ({ default: module.SheetsPage }))
);
const RequirementsPage = lazy(() =>
  import('@/features/requirements/pages/RequirementsPage').then((module) => ({ default: module.RequirementsPage }))
);
const AlbumReleasesPage = lazy(() =>
  import('@/features/albums/pages/AlbumReleasesPage').then((module) => ({ default: module.AlbumReleasesPage }))
);
const AlbumReleaseDetailPage = lazy(() =>
  import('@/features/albums/pages/AlbumReleaseDetailPage').then((module) => ({
    default: module.AlbumReleaseDetailPage,
  }))
);
const MembersPage = lazy(() =>
  import('@/features/workspace/pages/MembersPage').then((module) => ({ default: module.MembersPage }))
);
const ProjectSettingsPage = lazy(() =>
  import('@/features/workspace/pages/ProjectSettingsPage').then((module) => ({
    default: module.ProjectSettingsPage,
  }))
);
const ProjectHealthPage = lazy(() =>
  import('@/features/workspace/pages/ProjectHealthPage').then((module) => ({
    default: module.ProjectHealthPage,
  }))
);
const ProjectModelsPage = lazy(() =>
  import('@/features/workspace/pages/ProjectModelsPage').then((module) => ({
    default: module.ProjectModelsPage,
  }))
);
const DocumentDetailPage = lazy(() =>
  import('@/features/documents/pages/DocumentDetailPage').then((module) => ({
    default: module.DocumentDetailPage,
  }))
);
const MergeRequestsPage = lazy(() =>
  import('@/features/mergeRequests/pages/MergeRequestsPage').then((module) => ({
    default: module.MergeRequestsPage,
  }))
);
const CreateProductionRequestPage = lazy(() =>
  import('@/features/mergeRequests/pages/CreateProductionRequestPage').then((module) => ({
    default: module.CreateProductionRequestPage,
  }))
);
const MergeRequestDetailPage = lazy(() =>
  import('@/features/mergeRequests/pages/MergeRequestDetailPage').then((module) => ({
    default: module.MergeRequestDetailPage,
  }))
);
const LibraryAuditRunsPage = lazy(() =>
  import('@/features/audits/pages/LibraryAuditRunsPage').then((module) => ({
    default: module.LibraryAuditRunsPage,
  }))
);
const AuditsPage = lazy(() =>
  import('@/features/audits/pages/AuditsPage').then((module) => ({ default: module.AuditsPage }))
);
const CreateAuditPage = lazy(() =>
  import('@/features/audits/pages/CreateAuditPage').then((module) => ({
    default: module.CreateAuditPage,
  }))
);
const AuditResultsPage = lazy(() =>
  import('@/features/audits/pages/AuditResultsPage').then((module) => ({
    default: module.AuditResultsPage,
  }))
);
const AuditChainRunPage = lazy(() =>
  import('@/features/audits/pages/AuditChainRunPage').then((module) => ({
    default: module.AuditChainRunPage,
  }))
);
const AuditDetailPage = lazy(() =>
  import('@/features/audits/pages/AuditDetailPage').then((module) => ({
    default: module.AuditDetailPage,
  }))
);
const SnapshotsPage = lazy(() =>
  import('@/features/snapshots/pages/SnapshotsPage').then((module) => ({
    default: module.SnapshotsPage,
  }))
);
const SnapshotComparisonPage = lazy(() =>
  import('@/features/snapshots/pages/SnapshotComparisonPage').then((module) => ({
    default: module.SnapshotComparisonPage,
  }))
);
const DiffDetailPage = lazy(() =>
  import('@/features/snapshots/pages/DiffDetailPage').then((module) => ({
    default: module.DiffDetailPage,
  }))
);

function MessagePage({
  title,
  message,
}: {
  readonly title: string;
  readonly message: string;
}): React.JSX.Element {
  return (
    <Center flex={1} p="$6">
      <VStack space="sm" alignItems="center">
        <Heading size="lg">{title}</Heading>
        <Text size="sm" textAlign="center" color="$textLight600" $dark-color="$textDark400">
          {message}
        </Text>
      </VStack>
    </Center>
  );
}

/** The manifest lives in project Settings now. */
function ManifestRedirect(): React.JSX.Element {
  const { projectId = '' } = useParams();
  return <Navigate to={ROUTES.projectSettings(projectId)} replace />;
}

export function AppRouter(): React.JSX.Element {
  return (
    <>
      {/* Keeps the address Notifications emails current, once per signed-in session. */}
      <SubscriberSync />
      <Suspense fallback={<LoadingState label="Loading page…" testID="route-loading" />}>
        <Routes>
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route
            path={ROUTES.unauthorized}
            element={
              <MessagePage
                title="Access denied"
                message="Your account does not have permission to view that page. Contact an administrator if you believe this is a mistake."
              />
            }
          />

          {/* Everything below requires a session. */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AuthenticatedShell />}>
              <Route path={ROUTES.root} element={<Navigate to={ROUTES.dashboard} replace />} />
              <Route path={ROUTES.dashboard} element={<DashboardPage />} />
              <Route path={ROUTES.myTasks} element={<TasksPage />} />
              <Route
                path="/p/:projectId/tasks"
                element={
                  <WorkspaceGate>
                    <TasksPage />
                  </WorkspaceGate>
                }
              />
              <Route path={ROUTES.profile} element={<ProfilePage />} />
              <Route path={ROUTES.settings} element={<Navigate to={ROUTES.profile} replace />} />
              <Route path={ROUTES.notificationSettings} element={<NotificationSettingsPage />} />

              <Route path={ROUTES.families} element={<FamiliesPage />} />
              <Route path={ROUTE_PATTERNS.projectFamilyDetail} element={<ProjectFamilyDetailPage />} />
              <Route path={ROUTE_PATTERNS.familyDetail} element={<FamilyDetailPage />} />

              <Route path={ROUTES.projects} element={<LegacyModelListRoute fallback={<ProjectsPage />} />} />
              <Route
                path={ROUTE_PATTERNS.projectDetail}
                element={<LegacyModelRoute fallback={<ProjectDetailPage />} />}
              />

              <Route path={ROUTES.workspace} element={<WorkspaceProjectsPage />} />
              <Route path={ROUTE_PATTERNS.projectHome} element={<ProjectHomePage />} />
              <Route path={ROUTE_PATTERNS.projectModels} element={<ProjectModelsPage />} />
              <Route path={ROUTE_PATTERNS.projectHealth} element={<ProjectHealthPage />} />
              {/* A model and the pages it opens share one frame, so the model's tabs stay put. */}
              <Route
                path={ROUTE_PATTERNS.projectModel}
                element={
                  <WorkspaceGate>
                    <ModelFrame />
                  </WorkspaceGate>
                }
              >
                <Route index element={<ProjectDetailPage />} />
                <Route path="families/:familyId" element={<ProjectFamilyDetailPage />} />
                <Route path="timeline/compare" element={<SnapshotComparisonPage />} />
                <Route path="timeline/diffs/:diffId" element={<DiffDetailPage />} />
                <Route path="audits/runs/:instanceId" element={<AuditResultsPage />} />
                <Route path="audits/chain-runs/:chainRunId" element={<AuditChainRunPage />} />
                <Route path="audits/:auditId" element={<AuditDetailPage />} />
              </Route>
              <Route path={ROUTE_PATTERNS.projectDocuments} element={<DocumentsPage />} />
              <Route path={ROUTE_PATTERNS.documentDetail} element={<DocumentDetailPage />} />
              <Route path={ROUTE_PATTERNS.projectRequirements} element={<RequirementsPage />} />
              <Route path={ROUTE_PATTERNS.projectAlbumReleases} element={<AlbumReleasesPage />} />
              <Route path={ROUTE_PATTERNS.albumRelease} element={<AlbumReleaseDetailPage />} />
              <Route path={ROUTE_PATTERNS.projectSheets} element={<SheetsPage />} />
              <Route path={ROUTE_PATTERNS.projectManifest} element={<ManifestRedirect />} />
              <Route path={ROUTE_PATTERNS.projectMembers} element={<MembersPage />} />
              <Route path={ROUTE_PATTERNS.projectSettings} element={<ProjectSettingsPage />} />

              <Route path={ROUTES.mergeRequests} element={<MergeRequestsPage />} />
              <Route path={ROUTES.mergeRequestNewProduction} element={<CreateProductionRequestPage />} />
              <Route path={ROUTE_PATTERNS.mergeRequestDetail} element={<MergeRequestDetailPage />} />

              <Route path={ROUTES.audits} element={<AuditsPage />} />
              <Route path={ROUTES.auditLibraryRuns} element={<LibraryAuditRunsPage />} />
              <Route path={ROUTES.createAudit} element={<CreateAuditPage />} />
              <Route path={ROUTE_PATTERNS.auditResults} element={<AuditResultsPage />} />
              <Route path={ROUTE_PATTERNS.auditChainRun} element={<AuditChainRunPage />} />
              <Route path={ROUTE_PATTERNS.auditDetail} element={<AuditDetailPage />} />

              <Route path={ROUTES.snapshots} element={<SnapshotsPage />} />
              <Route path={ROUTE_PATTERNS.snapshotsForModel} element={<SnapshotComparisonPage />} />
              <Route path={ROUTE_PATTERNS.diffDetail} element={<DiffDetailPage />} />
              <Route path={ROUTES.compareSnapshots} element={<Navigate to={ROUTES.snapshots} replace />} />
            </Route>
          </Route>

          <Route
            path="*"
            element={
              <MessagePage
                title="Page not found"
                message="The page you requested does not exist or has been moved."
              />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}
